/**
 * Hook de acessibilidade pra modais (PR #44).
 *
 * Aplica em um <div> que represente o modal:
 *   - Captura ESC pra fechar
 *   - Focus trap: TAB e SHIFT+TAB ciclam dentro do modal
 *   - Foco inicial vai pro primeiro elemento focável (ou pro ref `initialFocus`)
 *   - Foco retorna pro elemento que abriu o modal quando fechar
 *
 * Uso:
 *   const modalRef = useRef<HTMLDivElement>(null);
 *   useModalA11y({ isOpen, onClose, modalRef });
 *
 *   <div ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="x">
 *     ...
 *   </div>
 */
import { useEffect, RefObject } from "react";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useModalA11y({
  isOpen,
  onClose,
  modalRef,
  initialFocusRef,
}: {
  isOpen: boolean;
  onClose: () => void;
  modalRef: RefObject<HTMLElement | null>;
  initialFocusRef?: RefObject<HTMLElement | null>;
}) {
  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      const root = modalRef.current;
      if (!root) return;

      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => el.offsetParent !== null); // visíveis

      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKey);

    // Foco inicial — espera o modal mountar
    const focusTimer = window.setTimeout(() => {
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus();
      } else if (modalRef.current) {
        const focusables = modalRef.current.querySelectorAll<HTMLElement>(
          FOCUSABLE_SELECTOR,
        );
        if (focusables.length > 0) focusables[0].focus();
      }
    }, 50);

    // Body scroll lock — evita scroll do conteúdo de fundo
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKey);
      window.clearTimeout(focusTimer);
      document.body.style.overflow = prevOverflow;
      // Devolve foco ao trigger
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, onClose, modalRef, initialFocusRef]);
}
